import { useRef, useState } from "react";
import { Button, ErrorText, Field, Panel, SelectField, Spinner } from "../../components/ui";
import { fmtDate } from "../../lib/format";
import {
  useCreateOpportunity,
  useDeleteMedia,
  useOpportunities,
  useUpdateOpportunity,
  useUploadMedia,
} from "./hooks";
import type { Opportunity, OpportunityInput } from "./api";

const TYPES = ["fcp", "action", "obligation", "ipo", "news", "education"];

const STATUS_ACTIONS: Record<string, { label: string; to: string }[]> = {
  draft: [{ label: "Publier", to: "published" }, { label: "Archiver", to: "archived" }],
  scheduled: [{ label: "Publier", to: "published" }, { label: "Brouillon", to: "draft" }],
  published: [{ label: "Archiver", to: "archived" }, { label: "Brouillon", to: "draft" }],
  archived: [{ label: "Republier", to: "published" }, { label: "Brouillon", to: "draft" }],
};

export function OpportunitiesPage() {
  const { data, isLoading, error } = useOpportunities();
  const create = useCreateOpportunity();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState<OpportunityInput | null>(null);

  const selected = data?.find((o) => o.id === selectedId) ?? null;

  return (
    <>
      <Panel
        title="Opportunités"
        actions={
          <Button
            onClick={() =>
              setCreating(creating ? null : { title: "", type: "fcp", summary: "" })
            }
          >
            {creating ? "Annuler" : "Nouvelle"}
          </Button>
        }
      >
        {creating && (
          <form
            className="inline-form"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate(
                { ...creating, summary: creating.summary || null },
                {
                  onSuccess: (opp) => {
                    setCreating(null);
                    setSelectedId(opp.id);
                  },
                },
              );
            }}
          >
            <Field
              label="Titre"
              value={creating.title}
              onChange={(e) => setCreating({ ...creating, title: e.target.value })}
              required
            />
            <SelectField
              label="Type"
              value={creating.type}
              onChange={(e) => setCreating({ ...creating, type: e.target.value })}
            >
              {TYPES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </SelectField>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "…" : "Créer (brouillon)"}
            </Button>
            <ErrorText error={create.error} />
          </form>
        )}

        <ErrorText error={error} />
        {isLoading ? (
          <Spinner />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Type</th>
                  <th>Statut</th>
                  <th>Vues</th>
                  <th>Publiée</th>
                </tr>
              </thead>
              <tbody>
                {data?.map((o) => (
                  <tr
                    key={o.id}
                    className={o.id === selectedId ? "row-active" : undefined}
                    onClick={() => setSelectedId(o.id === selectedId ? null : o.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <td>{o.title}</td>
                    <td>{o.type}</td>
                    <td>
                      <span className={`tag tag-${o.status}`}>{o.status}</span>
                    </td>
                    <td>{o.viewCount}</td>
                    <td>{fmtDate(o.publishedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {selected && <OpportunityEditor key={selected.id} opp={selected} />}
    </>
  );
}

function OpportunityEditor({ opp }: { opp: Opportunity }) {
  const update = useUpdateOpportunity();
  const upload = useUploadMedia();
  const removeMedia = useDeleteMedia();
  const fileRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    title: opp.title,
    summary: opp.summary ?? "",
    body: opp.body ?? "",
    ctaLabel: opp.ctaLabel,
    expectedYield: opp.expectedYield ?? "",
    minTicket: opp.minTicket ?? "",
  });

  function save(e: React.FormEvent) {
    e.preventDefault();
    update.mutate({
      id: opp.id,
      patch: {
        title: form.title,
        summary: form.summary || null,
        body: form.body || null,
        ctaLabel: form.ctaLabel,
        expectedYield: form.expectedYield || null,
        minTicket: form.minTicket || null,
      },
    });
  }

  return (
    <Panel
      title={`Édition — ${opp.title}`}
      actions={
        <div className="inline-form" style={{ margin: 0 }}>
          {(STATUS_ACTIONS[opp.status] ?? []).map((a) => (
            <Button
              key={a.to}
              variant="ghost"
              disabled={update.isPending}
              onClick={() => update.mutate({ id: opp.id, patch: { status: a.to } })}
            >
              {a.label}
            </Button>
          ))}
        </div>
      }
    >
      <form className="inline-form" onSubmit={save}>
        <Field
          label="Titre"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
        />
        <Field
          label="CTA"
          value={form.ctaLabel}
          onChange={(e) => setForm({ ...form, ctaLabel: e.target.value })}
        />
        <Field
          label="Rendement % (ex 7.5)"
          value={form.expectedYield}
          onChange={(e) => setForm({ ...form, expectedYield: e.target.value })}
        />
        <Field
          label="Ticket min (FCFA)"
          value={form.minTicket}
          onChange={(e) => setForm({ ...form, minTicket: e.target.value })}
        />
        <Button type="submit" disabled={update.isPending}>
          {update.isPending ? "…" : "Enregistrer"}
        </Button>
        <ErrorText error={update.error} />
      </form>

      <label className="field">
        <span>Résumé</span>
        <textarea
          rows={2}
          value={form.summary}
          onChange={(e) => setForm({ ...form, summary: e.target.value })}
        />
      </label>
      <label className="field">
        <span>Corps</span>
        <textarea
          rows={6}
          value={form.body}
          onChange={(e) => setForm({ ...form, body: e.target.value })}
        />
      </label>

      <h3>Médias</h3>
      <div className="media-grid">
        {opp.media.map((m) => (
          <figure key={m.id} className="media-item">
            {m.kind === "video" ? (
              m.thumbnailUrl ? (
                <img src={m.thumbnailUrl} alt={m.caption ?? ""} />
              ) : (
                <div className="media-placeholder">🎬</div>
              )
            ) : m.url ? (
              <img src={m.url} alt={m.caption ?? ""} />
            ) : null}
            {m.kind === "video" && (
              <span className={`tag tag-${m.status}`}>
                {m.status === "ready"
                  ? `vidéo · ${m.durationSeconds ?? "?"}s`
                  : m.status === "failed"
                    ? "échec transcodage"
                    : "transcodage…"}
              </span>
            )}
            <button
              className="btn btn-danger"
              disabled={removeMedia.isPending}
              onClick={() => removeMedia.mutate({ id: opp.id, mediaId: m.id })}
            >
              Supprimer
            </button>
          </figure>
        ))}
      </div>
      <label className="field">
        <span>Ajouter une image ou une vidéo</span>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,video/mp4,video/quicktime,video/webm"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file)
              upload.mutate(
                { id: opp.id, file },
                { onSuccess: () => fileRef.current && (fileRef.current.value = "") },
              );
          }}
        />
      </label>
      {upload.isPending && <p className="muted">Envoi… (les vidéos sont transcodées en arrière-plan)</p>}
      <ErrorText error={upload.error} />
    </Panel>
  );
}
